import React from 'react';
import { styles, colors } from '../theme';

// Phase 1 scaffold: a three-tile mode-select screen.
//
// Tiers (see srd/character-creation-plan.md §4.1):
//   1. Premade  — pick a fully-built character, name it, play (~30s).
//   2. Archetype — pick a curated archetype, customize lightly (~2 min). DEFAULT.
//   3. From Scratch — full step-by-step build (~5 min).
//   4. Advanced — toggle to unlock multiclass / manual ability scores / custom feats.
//
// Tiles currently render with no handlers wired — clicking does nothing.
// Phase 3 wires Tier 2 first; Phase 5 fills in Tiers 1 and 3.

const TILES = [
  {
    id: 'premade',
    title: 'Pick a Premade Hero',
    duration: '~30 seconds',
    description: 'Start with a fully-built character. Name them and play.',
    badge: 'Fastest',
  },
  {
    id: 'archetype',
    title: 'Start with an Archetype',
    duration: '~2 minutes',
    description: 'Pick a curated character archetype, then tweak what matters to you.',
    badge: 'Recommended',
    recommended: true,
  },
  {
    id: 'scratch',
    title: 'Build from Scratch',
    duration: '~5 minutes',
    description: 'Step through every choice — class, species, background, abilities.',
    badge: null,
  },
];

const Tile = ({ tile, onSelect }) => {
  const [hover, setHover] = React.useState(false);
  const recommended = tile.recommended;
  return (
    <button
      type="button"
      onClick={() => onSelect?.(tile.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      data-tile-id={tile.id}
      style={{
        ...styles.card,
        ...(hover ? styles.cardHover : {}),
        textAlign: 'left',
        cursor: 'pointer',
        width: '100%',
        outline: 'none',
        borderColor: recommended ? colors.accent : undefined,
        boxShadow: recommended
          ? `${styles.card.boxShadow}, 0 0 0 2px ${colors.accentGlow}`
          : styles.card.boxShadow,
      }}
    >
      {tile.badge ? (
        <div
          style={{
            display: 'inline-block',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 1,
            color: recommended ? colors.textOnAccent : colors.textSecondary,
            background: recommended ? colors.accent : 'transparent',
            border: recommended ? 'none' : `1px solid ${colors.borderLight}`,
            borderRadius: 4,
            padding: '2px 8px',
            marginBottom: 12,
          }}
        >
          {tile.badge}
        </div>
      ) : null}
      <h3
        style={{
          ...styles.heading,
          fontSize: 22,
          marginBottom: 4,
        }}
      >
        {tile.title}
      </h3>
      <div style={{ ...styles.textMuted, marginBottom: 12 }}>{tile.duration}</div>
      <p style={{ color: colors.textPrimary, margin: 0, lineHeight: 1.5 }}>
        {tile.description}
      </p>
    </button>
  );
};

const ModeSelectScreen = ({ onSelectMode, onAdvancedToggle, advancedEnabled = false }) => {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <header style={{ marginBottom: 48, textAlign: 'center' }}>
          <h1
            style={{
              ...styles.heading,
              fontSize: 40,
              letterSpacing: 1,
            }}
          >
            Create a Character
          </h1>
          <p style={styles.subheading}>How would you like to start?</p>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 20,
            marginBottom: 32,
          }}
          data-testid="mode-select-tiles"
        >
          {TILES.map((tile) => (
            <Tile key={tile.id} tile={tile} onSelect={onSelectMode} />
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            padding: '16px 0',
            borderTop: `1px solid ${colors.borderLight}`,
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: colors.textSecondary,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <input
              type="checkbox"
              checked={advancedEnabled}
              onChange={(e) => onAdvancedToggle?.(e.target.checked)}
              data-testid="advanced-toggle"
            />
            Show advanced options (multiclass, custom feats, manual ability allocation)
          </label>

          <span style={styles.textMuted}>
            Importing from elsewhere?{' '}
            <span style={{ fontStyle: 'italic' }}>Coming soon</span>
          </span>
        </div>

        <p style={styles.legalFootnote}>
          Character options derive from the System Reference Document 5.2.1 (&ldquo;SRD 5.2.1&rdquo;) and
          System Reference Document 5.1 (&ldquo;SRD 5.1&rdquo;) by Wizards of the Coast LLC, licensed under{' '}
          <a
            href="https://creativecommons.org/licenses/by/4.0/legalcode"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.accent }}
          >
            Creative Commons Attribution 4.0 International
          </a>.
        </p>
      </div>
    </div>
  );
};

export default ModeSelectScreen;
